/* eslint-disable @typescript-eslint/naming-convention */
import DE_Translations from './locales/de.json';
import EN_Translations from './locales/en.json';
import IT_Translations from './locales/it.json';
import FR_Translations from './locales/fr.json';
/* eslint-enable @typescript-eslint/naming-convention */
import * as z from 'zod';

// TODO: this code is quite messy and should be replaced with a proper i18n library

export const systemLanguages = ['de', 'en', 'it', 'fr'] as const;
export type SystemLanguage = (typeof systemLanguages)[number];

export const ANONYMOUS_LANGUAGE_STORAGE_KEY = 'anonymous_language';

export function isSystemLanguage(
  maybeSystemLanguage: string,
): maybeSystemLanguage is SystemLanguage {
  return systemLanguages.includes(maybeSystemLanguage as SystemLanguage);
}

// assuming DE contains the complete set of tranlsations
export type Translation = keyof typeof DE_Translations;
type TranslationFile = Record<Translation, string>;

export const systemLanguageLabelMap: Record<SystemLanguage, string> = {
  en: 'English',
  de: 'Deutsch',
  it: 'Italiano',
  fr: 'Français',
};

const languageFileMap: Record<SystemLanguage, Partial<TranslationFile>> = {
  en: EN_Translations,
  de: DE_Translations,
  it: IT_Translations,
  fr: FR_Translations,
};

type Placeholder = { id: string; value: string };
type PlaceholderInput = Placeholder[] | Record<string, string>;

const normalizePlaceholders = (
  placeholders?: PlaceholderInput,
): Placeholder[] => {
  if (!placeholders) {
    return [];
  }

  if (Array.isArray(placeholders)) {
    return placeholders;
  }

  return Object.entries(placeholders).map(([id, value]) => ({
    id,
    value,
  }));
};

export const $t = (
  stringIdentifier: Translation,
  placeholders?: PlaceholderInput,
  language: SystemLanguage = getUserLanguage(),
): string => {
  const selectedTranslationFile = languageFileMap[language];

  if (!selectedTranslationFile[stringIdentifier]) {
    return stringIdentifier;
  }

  let text = selectedTranslationFile[stringIdentifier];
  for (const placeholder of normalizePlaceholders(placeholders)) {
    text = text.replace(`{{${placeholder.id}}}`, placeholder.value);
  }
  return text;
};

export const $translateToLanguage = (
  language: SystemLanguage,
  stringIdentifier: Translation,
  placeholders?: PlaceholderInput,
): string => $t(stringIdentifier, placeholders, language);

/**
 * Reads a persisted item without ever touching a possibly-null
 * `localStorage` directly (which crashes in restricted WebViews / iframes such
 * as Teachino embedded in the Untis app).
 *
 * Prefers the shared safe-storage wrapper published on `globalThis` by
 * `@teachino/shared-util` (`safe-storage.ts`) so we read from the same backing
 * store the session store writes to — including its in-memory fallback. We
 * cannot import that module here because `shared-util` already depends on
 * `shared-i18n`, which would form a dependency cycle. The key literal must match
 * `GLOBAL_SAFE_LOCAL_STORAGE_KEY` in safe-storage.ts.
 */
function readPersistedItem(key: string): string | null {
  // Bracket access via a variable (not a string literal / dot access) keeps both
  // the dot-notation and naming-convention lint rules happy for this dunder key.
  const globalSafeStorageKey = '__teachinoSafeLocalStorage__';
  const sharedStorage = (
    globalThis as unknown as Record<
      string,
      { getItem(key: string): string | null } | undefined
    >
  )[globalSafeStorageKey];
  if (sharedStorage) {
    return sharedStorage.getItem(key);
  }
  // Fallback if safe-storage has not initialised yet: a defensively guarded
  // raw read that tolerates a null/throwing localStorage.
  try {
    const maybeStorage = localStorage as Storage | null | undefined;
    return maybeStorage ? maybeStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

/** The language of every new account until the teacher picks another one. */
export const DEFAULT_LANGUAGE: SystemLanguage = 'de';

// Built once: `getUserLanguage` runs on every `$t` call.
const anonymousLanguageSchema = z.object({
  language: z.enum(systemLanguages),
  expiresAt: z.number(),
});
const persistedUserSchema = z.object({
  language: z.string().min(1),
});

const getAnonymousLanguage = (): SystemLanguage | undefined => {
  try {
    const stored: unknown = JSON.parse(
      readPersistedItem(ANONYMOUS_LANGUAGE_STORAGE_KEY) ?? 'null',
    );
    const result = anonymousLanguageSchema.safeParse(stored);
    if (result.success && result.data.expiresAt > Date.now()) {
      return result.data.language;
    }
  } catch {
    return undefined;
  }
  return undefined;
};

/**
 * Language without a signed-in user: the invite language kept across the
 * anonymous signup steps and reloads, otherwise the default language.
 */
const getFallbackLanguage = (): SystemLanguage =>
  getAnonymousLanguage() ?? DEFAULT_LANGUAGE;

export function getUserLanguage(): SystemLanguage {
  // A signed-in user's persisted preference wins; the anonymous fallback is
  // only read when there is none.
  try {
    const user: unknown = JSON.parse(readPersistedItem('user') ?? '');
    const safeUser = persistedUserSchema.safeParse(user);
    if (!safeUser.success) {
      console.warn('Could not find User for translations');
      return getFallbackLanguage();
    }
    const { language } = safeUser.data;
    if (isSystemLanguage(language)) {
      return language;
    }
    const fallbackLanguage = getFallbackLanguage();
    console.warn(
      `Invalid language. Given: "${language}", falling back to language "${fallbackLanguage}"`,
    );
    return fallbackLanguage;
  } catch {
    return getFallbackLanguage();
  }
}

export const $pluralize = (
  stringIdentifier0: Translation,
  stringIdentifier1: Translation,
  stringIdentifierMany: Translation,
  number: number,
  // eslint-disable-next-line max-params
) => {
  if (number === 0) {
    return $t(stringIdentifier0, [{ id: 'number', value: String(number) }]);
  } else if (number === 1) {
    return $t(stringIdentifier1, [{ id: 'number', value: String(number) }]);
  } else {
    return $t(stringIdentifierMany, [{ id: 'number', value: String(number) }]);
  }
};
